import { Trans, useTranslation } from "react-i18next";
import AccountHeader from "../../AccountHeader";
import { Container, Stack, Typography, type ChipProps } from "@mui/material";
import { type ReactElement, useMemo } from "react";
import tKey from "../../../Common/Utils/tKey";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { useSearchParams } from "react-router";
import useStoreFlagError from "../../../Common/_hooks/Store/useStoreFlagError";
import StoreSetupCard from "./StoreSetupCard";
import StoreShippingTablesEmbed from "./StoreShippingTablesEmbed";
import StoreConnectionStepper from "./StoreConnectionStepper";
import useStoreData from "../../../Common/_hooks/Store/useStoreData";

export default function BigCartelConnect() {
  const [t] = useTranslation("account-v2");
  const [tR] = useTranslation("routes");

  const [searchParams] = useSearchParams();

  const storeSlug = "bigcartel";
  const setupUrl = tR("/account/stores/connect/{{store}}", { store: storeSlug });

  const store = useStoreData(storeSlug);

  const isFlagged = useMemo(() => store?.get("flagged"), [store]);
  const isMissingScope = !!store?.get("is_missing_scope");
  const flagErrorMessage = useStoreFlagError(storeSlug);
  const isConnected = !!store && !isFlagged && !isMissingScope;
  const connectUrl = `${setupUrl}?connect=1`;
  const startConnectUrl = tR("/d/{{store}}/connect", { store: storeSlug }) + "?connect=1";

  const connectErrorMessage = useMemo(() => {
    const error = searchParams.get("error");
    if (!error) return null;

    switch (error) {
      case "access_denied":
        return t("It looks like you canceled the Big Cartel authorization step. Follow the steps below again and click Allow to finish connecting your store.");
      case "already-exists":
        return t("This Big Cartel store is already linked to another Art of Where account. Please remove it from the other account first.");
      default:
        return t("An error occurred while connecting to your store. Please try again.");
    }
  }, [searchParams, t]);

  const connectionStatus = useMemo((): { label: string, color: ChipProps["color"], icon: ReactElement } => {
    if (isConnected) {
      return {
        label: t("Connected"),
        color: "success",
        icon: <CheckCircleIcon color="success" />,
      };
    }

    if (isFlagged) {
      return {
        label: t("Needs attention"),
        color: "error",
        icon: <ErrorOutlineRoundedIcon color="error" />,
      };
    }

    if (isMissingScope) {
      return {
        label: t("Needs attention"),
        color: "warning",
        icon: <ErrorOutlineRoundedIcon color="warning" />,
      };
    }

    return {
      label: t("Not connected"),
      color: "default",
      icon: <RadioButtonUncheckedRoundedIcon color="action" />,
    };
  }, [isConnected, isFlagged, isMissingScope, t]);

  return <div>
    <AccountHeader
      title={t("Set up your {{store}} store", { store: "Big Cartel" })}
    />

    <Container
      maxWidth="md"
      disableGutters
      style={{ margin: 0 }}
    >
      <Stack spacing={4}>
        <Typography variant="body1">
          {t("Connect your Big Cartel store first, then follow the shipping steps below to finish setting it up.")}
        </Typography>

        <StoreSetupCard
          icon={connectionStatus.icon}
          title={t("Connection")}
          status={connectionStatus.label}
          statusColor={connectionStatus.color}
        >
          <StoreConnectionStepper
            t={t}
            hasStoreStepLabel={t("Do you already have a Big Cartel store?")}
            noStoreTitle={t("Create your Big Cartel store")}
            noStoreDescription={<Typography variant="body1">
              <Trans
                t={t}
                i18nKey={tKey("Go to <a>Big Cartel</a> and create your storefront now.")}
                components={{
                  a: <a href="https://www.bigcartel.com/" target="_blank" />
                }}
              />
            </Typography>}
            connectStepLabel={t("Connect your Big Cartel store to Art of Where")}
            connectSteps={[
              t("Click the button below to start the Big Cartel authorization flow."),
              t("Sign in to your Big Cartel account if you are prompted to do so."),
              t("Review the requested permissions and click Allow to finish connecting your store."),
            ]}
            connectButtonLabel={t("Connect to Big Cartel")}
            connectUrl={connectUrl}
            startConnectRequested={searchParams.get("connect") === "1"}
            startConnectUrl={startConnectUrl}
            connectErrorMessage={connectErrorMessage}
            isConnected={isConnected}
            isFlagged={!!isFlagged}
            isMissingScope={isMissingScope}
            flagErrorMessage={flagErrorMessage}
            connectedContent={<Typography variant="body1">
              {t("Your Big Cartel store is connected and ready to receive orders.")}
            </Typography>}
          />
        </StoreSetupCard>

        <StoreSetupCard
          icon={<LocalShippingOutlinedIcon color="secondary" />}
          title={t("Shipping")}
          status={isConnected ? t("Manual step") : t("After connection")}
          statusColor={isConnected ? "warning" : "default"}
        >
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="body1">
              {t("Big Cartel does not use a separate shipping profile for this setup. When you add each product in Big Cartel, you enter the shipping price on that product directly for each country, along with the additional-item price.")}
            </Typography>

            <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
              <li>{t("Create or edit the product listing in Big Cartel.")}</li>
              <li>{t("In the product settings, enter the shipping price for each country where you want to sell, plus the additional-item price.")}</li>
              <li>{t("Use the Rates by product table below to copy the matching values into Big Cartel while you set up the product.")}</li>
            </ol>

            <StoreShippingTablesEmbed
              defaultView="product"
              allowedViews={["product"]}
              typeOptionsByCountry={{
                ca: [{ label: t("Standard"), value: "canada-expedited-parcel" }],
                us: [{ label: t("Standard"), value: "us-standard", zoneSourceValue: "usps-priority-mail" }],
                in: [
                  { label: t("International Tracked"), value: "intl-tracked" },
                  { label: t("DHL Express Worldwide"), value: "dhl-express-worldwide" },
                ],
              }}
            />
          </Stack>
        </StoreSetupCard>
      </Stack>
    </Container>
  </div>;
}
